import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CHIP_THEMES, ACCENT_BLUE, ACCENT_BLUE_DARK, SUCCESS_GREEN, ERROR_RED } from './qcTheme';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import LockIcon from '@mui/icons-material/Lock';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import { Question } from '../../../interfaces/question.interface';
import { QuestionAnswer } from '../../../interfaces/questionAnswer.interface';
import { AuditLog } from '../../../interfaces/auditLog.interface';
import { AssessmentSurvey, Comment } from '../../../interfaces/assessment.interface';
import { FileUpload } from '../../Forms/FileUpload';
import FileUploaderComp from '../../Forms/FileUpload';
import Comments from '../../Comments/Comments';
import AuditLogModal from '../../AuditLog/AuditLogModal';
import StandardDialog from '../../Modals/StandardDialog';
import { AssessorAnswer, PIDetails } from '../../../interfaces/assessment.interface';
import {
  FULLY_IMPLEMENTED,
  PARTIALLY_IMPLEMENTED,
  NOT_IMPLEMENTED,
  NOT_APPLICABLE,
} from '../../../services/surveyHelpers';
import { PLACEHOLDER_ANSWER_TEXTS } from '../../Admin/QC/qcAttention';
import {
  applyMention,
  findingSignature,
  formatSavedTime,
  getMentionMatches,
  getMentionTerm,
  getSaveStatusLine,
  getSpOverallLabel,
  groupSpControls,
  OrderedQuestionEntry,
  SpBucket,
} from './workspaceDerivations';
import QuestionMentionMenu from './QuestionMentionMenu';
import LinkedText from './LinkedText';
import TagInput from './TagInput';
import { useSaveFinding } from './useSaveFinding';

// Option-row colors (prototype OMETA) for the 2×2 answer grid.
const OPTION_COLORS: Record<string, { c: string; rgb: string }> = {
  [FULLY_IMPLEMENTED]: { c: SUCCESS_GREEN, rgb: '46,125,50' },
  [PARTIALLY_IMPLEMENTED]: { c: '#a86b00', rgb: '168,107,0' },
  [NOT_IMPLEMENTED]: { c: ERROR_RED, rgb: '211,47,47' },
  [NOT_APPLICABLE]: { c: '#616161', rgb: '97,97,97' },
};

const OPTION_ORDER = [FULLY_IMPLEMENTED, PARTIALLY_IMPLEMENTED, NOT_IMPLEMENTED, NOT_APPLICABLE];


const SUBCOLS: { value: string; label: string }[] = [
  { value: 'implemented', label: 'YES' },
  { value: 'compensating_control', label: 'C/C' },
  { value: 'not_implemented', label: 'N/I' },
  { value: 'not_applicable', label: 'N/A' },
];

const Caption = styled('p')({
  fontSize: '0.7rem',
  letterSpacing: '1px',
  fontWeight: 600,
  color: 'rgba(0,0,0,0.6)',
  textTransform: 'uppercase',
  margin: '0 0 10px',
});

const OptionsGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '10px',
});

const OptionRow = styled('div', {
  shouldForwardProp: prop => !['rgb', 'selected', 'disabledRow', 'interactive'].includes(prop as string),
})<{ rgb: string; selected: boolean; disabledRow: boolean; interactive: boolean }>(
  ({ rgb, selected, disabledRow, interactive }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '11px 14px',
    borderRadius: '6px',
    cursor: !interactive ? 'not-allowed' : disabledRow ? 'default' : 'pointer',
    opacity: disabledRow ? 0.5 : 1,
    transition: 'all 0.15s ease',
    // Admin read-only mirrors the SP Response treatment: dashed border, no shadow.
    background: !interactive
      ? `rgba(${rgb},0.07)`
      : selected ? `rgba(${rgb},0.10)` : `rgba(${rgb},0.06)`,
    // Selection reads through elevation and the dot; border stays 1.5px in every state so switching never shifts layout.
    border: !interactive
      ? `1.5px dashed rgba(${rgb},0.4)`
      : selected ? `1.5px solid rgba(${rgb},0.45)` : '1.5px solid transparent',
    boxShadow: interactive && selected ? `0 2px 6px rgba(${rgb},0.18)` : 'none',
  }),
);

// White fill keeps the ring/dot gap crisp on the tinted option rows.
const OptionDot = styled('span', {
  shouldForwardProp: prop => !['c', 'selected'].includes(prop as string),
})<{ c: string; selected: boolean }>(({ c, selected }) => ({
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  border: `2px solid ${selected ? c : 'rgba(0,0,0,0.3)'}`,
  background: '#fff',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.12s ease',
  '&::after': {
    content: '""',
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    background: selected ? c : 'transparent',
    transition: 'background 0.12s ease',
  },
}));

const OptionLabel = styled('span', {
  shouldForwardProp: prop => !['c', 'selected'].includes(prop as string),
})<{ c: string; selected: boolean }>(({ c, selected }) => ({
  fontSize: '0.9rem',
  color: selected ? c : 'rgba(0,0,0,0.87)',
  fontWeight: selected ? 600 : 400,
  whiteSpace: 'nowrap',
}));

const SpAnswerBadge = styled('span', {
  shouldForwardProp: prop => prop !== 'c',
})<{ c: string }>(({ c }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '5px',
  marginLeft: 'auto',
  height: '24px',
  padding: '0 10px',
  borderRadius: '12px',
  background: '#fff',
  border: `1.5px dashed ${c}`,
  color: c,
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  flex: 'none',
  boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
  '& .MuiSvgIcon-root': {
    fontSize: '12px',
  },
}));

// Sits inside the same 136px slot as the radios so the headers line up with them.
const SubColLabel = styled('span')({
  width: '18px',
  flex: 'none',
  textAlign: 'center',
  fontSize: '0.62rem',
  fontWeight: 600,
  color: 'rgba(0,0,0,0.6)',
});

const AttachmentLabel = styled('p')({
  fontSize: '0.875rem',
  color: 'rgba(0,0,0,0.75)',
  margin: '0 0 8px',
});

const ReadOnlyLock = styled(LockIcon)({
  fontSize: '15px',
  color: 'rgba(0,0,0,0.4)',
  marginLeft: 'auto',
  flex: 'none',
});

const MergedTable = styled('div')({
  marginTop: '16px',
  border: '1px solid rgba(0,0,0,0.1)',
  borderRadius: '6px',
  overflow: 'hidden',
});

// No flex gap — every cell centers, so insets from the column dividers are identical on both sides.
const TableHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  padding: '10px 14px 10px 0',
});

const ColLabel = styled('span', {
  shouldForwardProp: prop => prop !== 'w',
})<{ w?: number }>(({ w }) => ({
  width: w ? `${w}px` : undefined,
  flex: w ? 'none' : 1,
  fontSize: '0.7rem',
  letterSpacing: '0.5px',
  fontWeight: 600,
  color: 'rgba(0,0,0,0.6)',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  ...(w ? {
    display: 'inline-flex',
    justifyContent: 'center',
    alignSelf: 'stretch',
    alignItems: 'center',
    margin: '-10px 0',
    padding: '10px 0',
    borderRight: '1px solid rgba(0,0,0,0.08)',
  } : {}),
}));

const EditLink = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  border: 'none',
  background: 'none',
  padding: 0,
  fontFamily: 'inherit',
  fontSize: '0.75rem',
  fontWeight: 600,
  letterSpacing: '0.4px',
  textTransform: 'uppercase',
  color: ACCENT_BLUE,
  cursor: 'pointer',
  flex: 'none',
  '& .MuiSvgIcon-root': {
    fontSize: '14px',
  },
  '&:hover': {
    color: ACCENT_BLUE_DARK,
  },
});

const ControlRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  padding: '9px 14px 9px 0',
  borderTop: '1px solid rgba(0,0,0,0.06)',
});

// Negative margin lets the divider span the row's full height, past its padding.
const CellSlot = styled('span', {
  shouldForwardProp: prop => prop !== 'w',
})<{ w: number }>(({ w }) => ({
  width: `${w}px`,
  flex: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  alignSelf: 'stretch',
  margin: '-9px 0',
  padding: '9px 0',
  borderRight: '1px solid rgba(0,0,0,0.08)',
}));

// Borderless by design: dense read-only cells read as data with a flat tint; borders stay on status/filter chips.
const DispositionChip = styled('span', {
  shouldForwardProp: prop => !['rgb', 'tcolor'].includes(prop as string),
})<{ rgb: string; tcolor: string }>(({ rgb, tcolor }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '38px',
  height: '20px',
  padding: '0 8px',
  borderRadius: '10px',
  fontSize: '0.66rem',
  fontWeight: 700,
  letterSpacing: '0.3px',
  background: `rgba(${rgb},0.16)`,
  color: tcolor,
  boxSizing: 'border-box',
}));

const EmptyChip = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '38px',
  height: '20px',
  padding: '0 7px',
  borderRadius: '10px',
  fontSize: '0.66rem',
  fontWeight: 700,
  background: '#fff',
  color: 'rgba(0,0,0,0.35)',
  border: '1px dashed rgba(0,0,0,0.3)',
  boxSizing: 'border-box',
});

const ControlText = styled('span')({
  flex: 1,
  minWidth: 0,
  paddingLeft: '20px',
  fontSize: '0.9rem',
  fontWeight: 500,
  color: '#424242',
});

// Matches the R badge in AssessorPIDetails (Partially Implemented findings).
const RemediationBadge = styled('span')({
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  background: '#d32f2f',
  color: '#fff',
  fontSize: '12px',
  fontWeight: 'bold',
  lineHeight: 1,
  paddingLeft: '1px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  flex: 'none',
});

const MatrixRadio = styled('button', {
  shouldForwardProp: prop => prop !== 'selected',
})<{ selected: boolean }>(({ selected }) => ({
  width: '18px',
  height: '18px',
  borderRadius: '50%',
  border: `2px solid ${selected ? '#424242' : 'rgba(0,0,0,0.3)'}`,
  background: '#fff',
  padding: 0,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  transition: 'border-color 0.12s ease',
  '&::after': {
    content: '""',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: selected ? '#424242' : 'transparent',
    transition: 'background 0.12s ease',
  },
}));

const CommentToggle = styled('button')({
  border: 'none',
  background: 'none',
  padding: 0,
  cursor: 'pointer',
  width: '26px',
  flex: 'none',
  display: 'inline-flex',
  justifyContent: 'center',
});

const SubCommentField = styled('textarea')({
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid rgba(0,0,0,0.23)',
  borderRadius: '4px',
  padding: '6px 10px',
  fontFamily: 'Roboto, sans-serif',
  fontSize: '0.8125rem',
  color: 'rgba(0,0,0,0.87)',
  resize: 'vertical',
  outline: 'none',
  margin: '0 14px 8px',
  maxWidth: 'calc(100% - 28px)',
  '&:focus': {
    borderColor: ACCENT_BLUE,
  },
});

const DetailsRow = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '16px',
  flexWrap: 'wrap',
});

const DetailsBox = styled('div', {
  shouldForwardProp: prop => prop !== 'locked',
})<{ locked?: boolean }>(({ locked }) => ({
  flex: '1 1 280px',
  borderRadius: '4px',
  padding: '14px 16px',
  border: locked ? '1px solid rgba(0,0,0,0.15)' : '1px solid rgba(0,0,0,0.08)',
  background: locked ? 'rgba(0,0,0,0.02)' : '#fafbfe',
}));

const DetailsText = styled('p')({
  fontSize: '0.8125rem',
  fontStyle: 'italic',
  color: 'rgba(0,0,0,0.7)',
  margin: 0,
});

const EvidenceCluster = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  flex: 'none',
  // Hug the bottom edge of the details box rather than floating mid-row.
  alignSelf: 'flex-end',
});

const EvidenceButtons = styled('div')({
  display: 'flex',
  gap: '10px',
});

const OutlinedAction = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  background: '#fff',
  border: '1px solid rgba(25,118,210,0.5)',
  color: ACCENT_BLUE,
  borderRadius: '4px',
  padding: '6px 12px',
  fontSize: '0.75rem',
  fontWeight: 500,
  letterSpacing: '0.46px',
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontFamily: 'inherit',
  '& .MuiSvgIcon-root': {
    fontSize: '16px',
  },
  '&:hover': {
    borderColor: ACCENT_BLUE,
    background: 'rgba(25,118,210,0.04)',
  },
});

const FindingsTextarea = styled('textarea', {
  shouldForwardProp: prop => prop !== 'locked',
})<{ locked: boolean }>(({ locked }) => ({
  width: '100%',
  boxSizing: 'border-box',
  minHeight: '132px',
  borderRadius: '4px',
  padding: '10px 12px',
  fontFamily: 'Roboto, sans-serif',
  fontSize: '0.9rem',
  outline: 'none',
  ...(locked ? {
    // Identical to the locked SP Additional Details box (Moore, 2026-08-27).
    border: '1px solid rgba(0,0,0,0.15)',
    color: 'rgba(0,0,0,0.75)',
    background: 'rgba(0,0,0,0.02)',
    resize: 'none',
    cursor: 'default',
  } : {
    border: '1px solid rgba(0,0,0,0.42)',
    color: 'rgba(0,0,0,0.87)',
    resize: 'vertical',
    '&:focus': {
      borderColor: ACCENT_BLUE,
      boxShadow: '0 0 0 2px rgba(25,118,210,0.2)',
    },
  }),
}));

// Read-only twin of the locked FindingsTextarea — a textarea can't hold clickable #question links.
const FindingsDisplay = styled('div')({
  width: '100%',
  boxSizing: 'border-box',
  minHeight: '132px',
  borderRadius: '4px',
  padding: '10px 12px',
  fontFamily: 'Roboto, sans-serif',
  fontSize: '0.9rem',
  border: '1px solid rgba(0,0,0,0.15)',
  color: 'rgba(0,0,0,0.75)',
  background: 'rgba(0,0,0,0.02)',
  whiteSpace: 'pre-wrap',
  overflowWrap: 'break-word',
});

const SaveRow = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '8px',
  marginTop: '16px',
});

const SaveButton = styled('button')({
  border: 'none',
  borderRadius: '4px',
  padding: '8px 16px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '0.8125rem',
  fontWeight: 500,
  letterSpacing: '0.46px',
  textTransform: 'uppercase',
  fontFamily: 'Roboto, sans-serif',
  cursor: 'pointer',
  background: ACCENT_BLUE,
  color: '#fff',
  boxShadow: '0 3px 1px -2px rgba(0,0,0,0.2),0 2px 2px 0 rgba(0,0,0,0.14),0 1px 5px 0 rgba(0,0,0,0.12)',
  '&:disabled': {
    background: 'rgba(0,0,0,0.12)',
    color: 'rgba(0,0,0,0.38)',
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
});

const StatusLine = styled('span', {
  shouldForwardProp: prop => prop !== 'lineColor',
})<{ lineColor: string }>(({ lineColor }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '0.78rem',
  fontWeight: 500,
  color: lineColor,
  '&::before': {
    content: '""',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: lineColor,
  },
}));

const ErrorLine = styled('p')({
  fontSize: '0.78rem',
  color: ERROR_RED,
  margin: '8px 0 0',
});

// Prototype asrMarkFor: the overall answer implies every control; only Partially Implemented reads per-control disposition.
function assessorDisposition(answer: string | undefined, detail: PIDetails | undefined): string | null {
  if (answer === undefined) return null;
  if (answer === FULLY_IMPLEMENTED) return 'implemented';
  if (answer === NOT_IMPLEMENTED) return 'not_implemented';
  if (answer === NOT_APPLICABLE) return 'not_applicable';
  return detail?.compensatingOrNotApplicable || null;
}

export interface AssessorAnswerPanelProps {
  question: Question;
  questionAnswer?: QuestionAnswer;
  auditLogs?: { [key: string]: AuditLog };
  evidenceFiles?: FileUpload[];
  comments?: Comment[];
  assessmentSurvey?: AssessmentSurvey;
  commentsDisabled?: boolean;
  onPostComment?: (text: string, attachments: number[]) => Promise<void>;
  existing?: AssessorAnswer;
  editable: boolean;
  isFiveThreeOrGreater: boolean;
  tags: string[];
  entries?: OrderedQuestionEntry[];
  sessionSave?: { by: string; at: number };
  onAddTag: (tag: string) => void;
  onRemoveTag: (tag: string) => void;
  onNavigateToCode?: (shortCode: string) => void;
  onFindingSaved?: () => void;
}

export default function AssessorAnswerPanel(props: AssessorAnswerPanelProps): JSX.Element {
  const {
    question, questionAnswer, auditLogs, evidenceFiles, comments,
    assessmentSurvey, commentsDisabled, onPostComment,
    existing, editable, isFiveThreeOrGreater, tags,
    entries = [], sessionSave, onAddTag, onRemoveTag,
    onNavigateToCode = () => undefined, onFindingSaved,
  } = props;
  const { saveFinding, isSaving } = useSaveFinding();
  const [answer, setAnswer] = useState<string | undefined>(existing?.answer);
  const [finding, setFinding] = useState(existing?.assessorFinding || '');
  const [ctrlAns, setCtrlAns] = useState<{ [key: string]: PIDetails }>({});
  const [openComments, setOpenComments] = useState<Record<number, boolean>>({});
  const [auditOpen, setAuditOpen] = useState(false);
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [savedSig, setSavedSig] = useState<string | null>(null);
  const [forceEdit, setForceEdit] = useState(false);
  const [error, setError] = useState('');
  const [mentionDismissed, setMentionDismissed] = useState(false);

  const subControls = useMemo(
    () => question.answers.filter(a => !PLACEHOLDER_ANSWER_TEXTS.includes(a.text || '')),
    [question.answers],
  );
  const isSingleSelect = question.type === 'single_select';

  const spDispositions = useMemo(() => {
    const byControl: Record<number, SpBucket> = {};
    groupSpControls(question, questionAnswer).forEach(group => {
      group.controls.forEach(control => { byControl[control.id] = group.bucket; });
    });
    return byControl;
  }, [question, questionAnswer]);

  const spOverall = getSpOverallLabel(question, questionAnswer);

  // Restores the draft to the last saved record. Used on question change and by Cancel.
  const resetDraftToSaved = useCallback(() => {
    setAnswer(existing?.answer);
    setFinding(existing?.assessorFinding || '');
    setError('');
    setOpenComments({});
    setForceEdit(false);
    const hydrated = (existing?.assessorQuestionAnswers || []).reduce((acc, aqa) => {
      acc[aqa.answer as number] = {
        id: aqa.id,
        answer: aqa.answer,
        comment: aqa.comment || '',
        isImplemented: aqa.isImplemented || false,
        compensatingOrNotApplicable: aqa.compensatingOrNotApplicable || '',
      };
      return acc;
    }, {} as { [key: string]: PIDetails });
    if (Object.keys(hydrated).length) {
      setCtrlAns(hydrated);
    } else {
      // No saved assessor answer yet: mirror the SP's disposition per control.
      setCtrlAns(subControls.reduce((acc, control) => {
        const spBucket = spDispositions[control.id];
        acc[control.id] = {
          answer: control.id,
          comment: '',
          isImplemented: false,
          compensatingOrNotApplicable: spBucket && spBucket !== 'unanswered' ? spBucket : '',
        };
        return acc;
      }, {} as { [key: string]: PIDetails }));
    }
    setSavedSig(existing?.id ? findingSignature(existing.answer, existing.assessorFinding, []) : null);
  }, [question.id, existing?.id, subControls, spDispositions]);

  // Sync local draft from the store record when the question changes (pure read).
  useEffect(() => {
    resetDraftToSaved();
  }, [question.id, existing?.id]);

  const currentSig = findingSignature(answer, finding, []);
  const dirty = savedSig !== currentSig;
  const canSave = editable && answer !== undefined && finding.trim().length > 0 && dirty && !isSaving;
  const mentionTerm = mentionDismissed || !editable ? null : getMentionTerm(finding);
  const mentionMatches = mentionTerm === null ? [] : getMentionMatches(entries, mentionTerm, 5);

  // Same lookup the v1 questionnaire uses; entries are matched by answer id.
  const questionAuditLogs = useMemo<AuditLog[]>(() => {
    if (!auditLogs) return [];
    const qaId = questionAnswer?.id;
    const aaId = existing?.id;
    if (!qaId && !aaId) return [];
    return Object.values(auditLogs)
      .filter(log => (qaId && log.questionAnswer === qaId) || (aaId && log.assessorAnswer === aaId))
      .sort((a, b) => ((a.createdAt || '') < (b.createdAt || '') ? 1 : -1));
  }, [auditLogs, questionAnswer?.id, existing?.id]);

  const statusLine = getSaveStatusLine(dirty, sessionSave, !!existing?.id);
  const statusText = statusLine.kind === 'saved' ? `Saved by ${statusLine.by} at ${formatSavedTime(statusLine.at)}`
    : statusLine.kind === 'saved_previously' ? 'Saved'
      : statusLine.kind === 'unsaved_changes' ? 'Unsaved changes'
        : answer !== undefined && !finding.trim() ? 'Assessor findings required to save' : 'Not yet saved';
  const statusColor = statusLine.kind === 'saved' || statusLine.kind === 'saved_previously' ? SUCCESS_GREEN : '#ffab00';

  // Radios show while a Partially Implemented answer is being worked; they collapse to chips once saved clean.
  const savedClean = !dirty && (!!sessionSave || !!existing?.id) && !forceEdit;
  const radioMode = editable && isFiveThreeOrGreater && answer === PARTIALLY_IMPLEMENTED && !savedClean;
  const showEditLink = editable && isFiveThreeOrGreater && answer === PARTIALLY_IMPLEMENTED && savedClean;
  // Mid-edit escape hatch: only offered when there is a saved record to revert to.
  const showCancelLink = editable && isFiveThreeOrGreater && answer === PARTIALLY_IMPLEMENTED
    && !!existing?.id && (forceEdit || dirty);

  const handleSave = async () => {
    setError('');
    const result = await saveFinding({ question, assessorAnswer: answer!, assessorFinding: finding, questionCtrlAns: ctrlAns });
    if (result.ok) {
      setSavedSig(currentSig);
      setForceEdit(false);
      onFindingSaved?.();
    } else {
      setError(result.error);
    }
  };

  const setDisposition = (answerId: number, value: string) => {
    setCtrlAns(prev => ({
      ...prev,
      [answerId]: { ...prev[answerId], answer: answerId, compensatingOrNotApplicable: value },
    }));
  };

  // Counts the same filtered list the dialog renders (v1's `Attachments ({files.length})`).
  const evidenceCount = evidenceFiles?.length || 0;
  // Same source the v1 questionnaire counts (assessmentQuestionsStatus[q].comments).
  const commentCount = existing?.comments?.length || 0;

  return (
    <div>
      <Caption>Assessor Answer</Caption>
      <OptionsGrid>
        {/* Admin personas see the answer read-only in grouped format: selected option only. */}
        {(editable ? OPTION_ORDER : OPTION_ORDER.filter(option => option === answer)).map(option => {
          const colors = OPTION_COLORS[option];
          const selected = answer === option;
          const disabledRow = editable && option === PARTIALLY_IMPLEMENTED && isSingleSelect;
          const isSpAnswer = spOverall === option;
          return (
            <OptionRow
              key={option}
              rgb={colors.rgb}
              selected={selected}
              disabledRow={disabledRow}
              interactive={editable}
              role='radio'
              aria-checked={selected}
              aria-disabled={!editable || disabledRow}
              onClick={() => { if (editable && !disabledRow) setAnswer(option); }}
            >
              <OptionDot c={colors.c} selected={selected} />
              <OptionLabel c={colors.c} selected={selected}>{option}</OptionLabel>
              {!editable && !isSpAnswer && <ReadOnlyLock />}
              {isSpAnswer && (
                <SpAnswerBadge c={colors.c}>
                  <LockIcon />
                  SP Answer
                </SpAnswerBadge>
              )}
            </OptionRow>
          );
        })}
      </OptionsGrid>
      {subControls.length > 0 && (
        <MergedTable>
          <TableHeader>
            <ColLabel w={78}>SP</ColLabel>
            <ColLabel w={78}>Assessor</ColLabel>
            {radioMode && (
              <CellSlot w={136} sx={{ gap: '8px' }}>
                {SUBCOLS.map(col => (
                  <SubColLabel key={col.value}>{col.label}</SubColLabel>
                ))}
              </CellSlot>
            )}
            <ColLabel sx={{ paddingLeft: '20px' }}>Control</ColLabel>
            {showEditLink && (
              <EditLink type='button' onClick={() => setForceEdit(true)}>
                <EditIcon />
                Edit
              </EditLink>
            )}
            {showCancelLink && (
              <EditLink type='button' onClick={resetDraftToSaved} sx={{ color: 'rgba(0,0,0,0.6)' }}>
                <CloseIcon />
                Cancel
              </EditLink>
            )}
          </TableHeader>
          {subControls.map(control => {
            const spBucket = spDispositions[control.id];
            const spTheme = spBucket && spBucket !== 'unanswered' ? CHIP_THEMES[spBucket] : undefined;
            const detail = ctrlAns[control.id];
            const asrDisposition = assessorDisposition(answer, detail);
            const asrTheme = asrDisposition ? CHIP_THEMES[asrDisposition] : undefined;
            const needsRemediation = ['compensating_control', 'not_implemented'].includes(asrDisposition || '');
            return (
              <React.Fragment key={control.id}>
                <ControlRow>
                  <CellSlot w={78}>
                    {spTheme ? (
                      <DispositionChip rgb={spTheme.rgb} tcolor={spTheme.text}>{spTheme.label}</DispositionChip>
                    ) : <EmptyChip>—</EmptyChip>}
                  </CellSlot>
                  <CellSlot w={78}>
                    {asrTheme ? (
                      <DispositionChip rgb={asrTheme.rgb} tcolor={asrTheme.text}>{asrTheme.label}</DispositionChip>
                    ) : <EmptyChip>—</EmptyChip>}
                  </CellSlot>
                  {radioMode && (
                    <CellSlot w={136} sx={{ gap: '8px' }}>
                      {SUBCOLS.map(col => (
                        <MatrixRadio
                          key={col.value}
                          type='button'
                          selected={detail?.compensatingOrNotApplicable === col.value}
                          aria-label={`${control.text} ${col.label}`}
                          onClick={() => setDisposition(control.id, col.value)}
                        />
                      ))}
                    </CellSlot>
                  )}
                  <ControlText>{control.text}</ControlText>
                  {radioMode && (
                    <CommentToggle
                      type='button'
                      aria-label={`Comment on ${control.text}`}
                      onClick={() => setOpenComments(prev => ({ ...prev, [control.id]: !prev[control.id] }))}
                    >
                      {detail?.comment || openComments[control.id]
                        ? <ChatBubbleIcon sx={{ fontSize: 17, color: ACCENT_BLUE }} />
                        : <ChatBubbleOutlineIcon sx={{ fontSize: 17, color: 'rgba(0,0,0,0.4)' }} />}
                    </CommentToggle>
                  )}
                  {needsRemediation && (
                    <Tooltip title='Generates a remediation item'>
                      <RemediationBadge>R</RemediationBadge>
                    </Tooltip>
                  )}
                </ControlRow>
                {radioMode && openComments[control.id] && (
                  <SubCommentField
                    rows={2}
                    value={detail?.comment || ''}
                    placeholder='Add a comment for this control'
                    onChange={e => setCtrlAns(prev => ({
                      ...prev,
                      [control.id]: { ...prev[control.id], answer: control.id, comment: e.target.value },
                    }))}
                  />
                )}
              </React.Fragment>
            );
          })}
        </MergedTable>
      )}
      <Caption sx={{ margin: '16px 0 6px' }}>SP Additional Details</Caption>
      <DetailsRow>
          <DetailsBox locked={!editable}>
            <DetailsText sx={questionAnswer?.text ? undefined : { color: 'rgba(0,0,0,0.4)', fontStyle: 'italic' }}>
              {questionAnswer?.text || 'None provided.'}
            </DetailsText>
          </DetailsBox>
          <EvidenceCluster>
            <EvidenceButtons>
              <OutlinedAction type='button' onClick={() => setEvidenceOpen(true)}>
                <AttachFileIcon />
                Evidence ({evidenceCount})
              </OutlinedAction>
              <OutlinedAction type='button' onClick={() => setCommentsOpen(true)}>
                <ChatBubbleOutlineIcon />
                Comments ({commentCount})
              </OutlinedAction>
            </EvidenceButtons>
          </EvidenceCluster>
      </DetailsRow>
      <Caption sx={{ margin: '16px 0 6px' }}>Assessor Findings</Caption>
      {editable ? (
        <>
          <FindingsTextarea
            locked={false}
            value={finding}
            placeholder='Describe what evidence you verified, or what was missing. Type # to link a question. (required)'
            onChange={e => { setFinding(e.target.value); setMentionDismissed(false); }}
            onKeyDown={e => { if (e.key === 'Escape') setMentionDismissed(true); }}
          />
          <QuestionMentionMenu
            matches={mentionMatches}
            onPick={shortCode => { setFinding(prev => applyMention(prev, shortCode)); setMentionDismissed(false); }}
          />
        </>
      ) : (
        <FindingsDisplay aria-label='Assessor findings'>
          <LinkedText text={finding} entries={entries} onNavigateToCode={onNavigateToCode} />
        </FindingsDisplay>
      )}
      <Caption sx={{ margin: '16px 0 8px' }}>Tags</Caption>
      <TagInput tags={tags} editable={editable} onAdd={onAddTag} onRemove={onRemoveTag} />
      {editable && (
        <SaveRow>
          <SaveButton type='button' disabled={!canSave} onClick={handleSave}>
            {isSaving && <CircularProgress size={16} color='inherit' />}
            {isSaving ? 'Saving…' : 'Save Finding'}
          </SaveButton>
          <StatusLine
            lineColor={statusColor}
            onClick={() => { if (questionAuditLogs.length) setAuditOpen(true); }}
            sx={questionAuditLogs.length ? { cursor: 'pointer', '&:hover': { textDecoration: 'underline' } } : undefined}
          >
            {statusText}
          </StatusLine>
        </SaveRow>
      )}
      {error && <ErrorLine>{error}</ErrorLine>}
      <StandardDialog
        title={`Upload attachments to question: ${question.title}`}
        handleClose={() => setEvidenceOpen(false)}
        isOpen={evidenceOpen}
      >
        {question.attachmentFormLabel && <AttachmentLabel>{question.attachmentFormLabel}</AttachmentLabel>}
        <FileUploaderComp
          disabled
          showCOPublicModal
          assessorVisible
          uploadType={'evidence'}
          fileList={evidenceFiles || []}
          callback={() => {}}
          dontShowUpload
          enablePublic
        />
      </StandardDialog>
      <StandardDialog
        title={`Comments for Question: ${question.title}`}
        handleClose={() => setCommentsOpen(false)}
        isOpen={commentsOpen}
      >
        <Comments
          disabled={commentsDisabled}
          comments={comments}
          handleClose={() => setCommentsOpen(false)}
          postComment={onPostComment}
          assessmentSurvey={assessmentSurvey}
        />
      </StandardDialog>
      <StandardDialog
        title={`Audit Logs for Question: ${question.title}`}
        handleClose={() => setAuditOpen(false)}
        isOpen={auditOpen}
        maxWidth='lg'
      >
        <AuditLogModal auditLogs={questionAuditLogs} handleClose={() => setAuditOpen(false)} />
      </StandardDialog>
    </div>
  );
}
